// Strips the invisible junk that rides along when an address is pasted from
// a webpage, PDF, or another CRM: zero-width spaces, BOMs, word joiners,
// non-breaking spaces, and directional marks. These characters are literally
// unseeable in the UI but make the address bounce ("Address not found") —
// Henry hit this 2026-08-01 with a pasted Gmail address whose leading
// zero-width space showed up as "Ã¢Â€Â‹" in the bounce notice.
//
// Also handles the already-mangled form: when a UTF-8 zero-width space gets
// read as Latin-1 it becomes the literal characters "â€‹", which is what
// lands in the database if the mojibake was pasted rather than the original.

// U+200B..U+200D zero-width, U+FEFF BOM, U+2060 word joiner,
// U+00A0 non-breaking space, U+200E/U+200F directional marks.
const INVISIBLE = /[​-‍﻿⁠ ‎‏]/g;
// The mojibake spellings of those same characters.
const MOJIBAKE = /(â€‹|â€Œ|â€|Ã¢Â€Â‹|Â )/g;

export function cleanEmail(value) {
  if (value == null) return "";
  return String(value)
    .replace(INVISIBLE, "")
    .replace(MOJIBAKE, "")
    .trim();
}

// The form a staff address is STORED in: hidden characters stripped, trimmed,
// and lowercased. Firestore equality is byte-exact and Google hands the
// panel and the login flow a lowercase address, so a users record saved as
// "Jane.Smith@TeamEdelstein.com" matched nothing and locked that agent out of
// the Gmail panel on every open (found 2026-09-23). Every write of
// users.email goes through this; every reader can then compare plainly.
export function normalizeEmail(value) {
  return cleanEmail(value).toLowerCase();
}

// The staff records whose stored email differs from its normalised form:
// what the owner's one-time tidy (Settings > Super Admin > Email Addresses)
// previews and then writes. Each row carries the name for the preview, the
// address as stored, and the address as it will be saved.
export function emailFixes(users) {
  return (users || [])
    .map((u) => ({
      id: u && u.id,
      name:
        [u && u.firstname, u && u.lastname].filter(Boolean).join(" ") ||
        (u && u.email) ||
        (u && u.id) ||
        "",
      from: String((u && u.email) ?? ""),
      to: normalizeEmail(u && u.email),
    }))
    .filter((f) => f.id && f.to && f.from !== f.to);
}

// True when the value contains hidden characters that would break delivery.
// Used to warn on contacts saved before the cleaning existed.
export function hasHiddenCharacters(value) {
  if (value == null) return false;
  const s = String(value);
  return INVISIBLE.test(s) || MOJIBAKE.test(s) || s !== s.trim();
}
